import { useQuery } from '@tanstack/react-query';
import { useParams, useSearchParams, useNavigate, useLocation } from 'react-router';
import { useCallback } from 'react';
import { publicApi } from '@/lib/api/public';
import type { PublicTenant } from '@/lib/api/schemas';

export function useSlug(): string {
  const { slug = '' } = useParams();
  return slug.toLowerCase();
}

export function useTenant() {
  const slug = useSlug();
  return useQuery({
    queryKey: ['tenant', slug],
    queryFn: () => publicApi.tenant(slug),
    staleTime: 5 * 60_000,
    enabled: slug.length > 0,
  });
}

/** Студия уже загружена в PublicLayout — компоненты внутри получают её синхронно */
export function useLoadedTenant(): PublicTenant {
  const { data } = useTenant();
  if (!data) throw new Error('Tenant is not loaded');
  return data;
}

export function photosOf(t: PublicTenant, kind: 'hero' | 'logo' | 'work' | 'service') {
  return t.photos.filter((p) => p.kind === kind);
}

export type BookStep = 'service' | 'time' | 'contact' | 'confirm' | 'done';
const ORDER: BookStep[] = ['service', 'time', 'contact', 'confirm', 'done'];

/**
 * Состояние шторки записи хранится в URL (?book=step&svc=…&at=…), каждый шаг —
 * запись истории. Кнопка «Назад» телефона возвращает на предыдущий шаг,
 * а затем закрывает шторку.
 */
export function useBookingRoute() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const step = (params.get('book') as BookStep | null) ?? null;
  const serviceId = params.get('svc');
  const startsAt = params.get('at');
  const depth = (location.state as { bookDepth?: number } | null)?.bookDepth ?? 0;

  const go = useCallback(
    (next: BookStep, extra: Record<string, string | null> = {}, opts: { replace?: boolean } = {}) => {
      const p = new URLSearchParams(params);
      p.set('book', next);
      for (const [k, v] of Object.entries(extra)) {
        if (v === null) p.delete(k);
        else p.set(k, v);
      }
      navigate(
        { pathname: location.pathname, search: `?${p.toString()}` },
        { replace: opts.replace ?? false, state: { bookDepth: opts.replace ? depth : depth + 1 } },
      );
    },
    [params, navigate, location.pathname, depth],
  );

  const close = useCallback(() => {
    if (depth > 0) navigate(-depth);
    else navigate({ pathname: location.pathname, search: '' }, { replace: true });
  }, [depth, navigate, location.pathname]);

  const back = useCallback(() => {
    if (depth > 1) navigate(-1);
    else {
      const i = step ? ORDER.indexOf(step) : 0;
      const prev = ORDER[Math.max(0, i - 1)] ?? 'service';
      go(prev, {}, { replace: true });
    }
  }, [depth, navigate, step, go]);

  return { step, serviceId, startsAt, go, close, back, open: step !== null };
}
